import api from "./config";

// Import chatbot service for AI-powered chat functionality
import { chatbotService } from "./chatbotService";

// USER API
export const signUp = (userData) =>
    api.post("/users/signup", userData);
export const signIn = (userData) =>
    api.post("/users/signin", userData);
export const demoSendOTP = (email) =>
    api.post("/users/demo-send-otp", { email });
export const demoVerifyOTP = (email, code, subscribeToUpdates = false) =>
    api.post("/users/demo-verify-otp", { email, code, subscribeToUpdates });
export const demoUpdateSubscription = (email, subscribed) =>
    api.post("/users/demo-update-subscription", { email, subscribed });
export const consumeDemoTrial = () =>
    api.post("/users/demo-consume-trial", {});
export const createLemonSqueezyCheckout = (payload) =>
    api.post('/payments/lemon-squeezy/checkout', payload);
export const syncLemonSqueezyOrder = (orderId, packageId) =>
    api.post('/payments/lemon-squeezy/sync', { orderId, packageId });
export const getLemonSqueezyAttemptStatus = (attemptId) =>
    api.get(`/payments/lemon-squeezy/attempts/${attemptId}/status`);
export const reconcileLemonSqueezyAttempt = (attemptId) =>
    api.post(`/payments/lemon-squeezy/attempts/${attemptId}/reconcile`);
export const submitSupportRequest = (requestType, payload) =>
    api.post('/users/support-request', { requestType, payload });
export const logout = () =>
    api.post("/users/logout");
export const refreshToken = () =>
    api.post("/users/refresh-token", {});
export const checkAuthStatus = () =>
    api.get("/users/check-auth");
export const forgotPassword = (email) =>
    api.post("/users/forgot-password", { email });
export const resetPassword = (resetData) =>
    api.post("/users/reset-password", resetData);
export const resendConfirmation = (email) =>
    api.post("/users/resend-confirmation", { email });
export const getUsers = () => api.get("/users");
export const getUserById = (id) => api.get(`/users/${id}`);

/**
 * Update user information
 * @param {string} id - User ID
 * @param {Object} userData - Updated user data
 */
export const updateUser = (id, userData) =>
    api.put(`/users/${id}`, userData);

/**
 * Upload avatar image
 * @param {FormData} formData - FormData containing avatar file
 */
export const uploadAvatar = (formData) =>
    api.post('/upload/avatar', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

/**
 * Delete a user
 * @param {string} id - User ID
 */
export const deleteUser = (id) =>
    api.delete(`/users/${id}`);

// QUESTION API
/**
 * Create a new question
 * @param {Object} questionData - Question data
 */
export const createQuestion = (questionData) =>
    api.post("/questions", questionData);

/**
 * Get all questions
 */
export const getQuestions = () => api.get("/questions");

/**
 * Get a question for practice based on section and user
 * @param {string} section - Question section (e.g., "Reading", "Writing")
 * @param {string} userId - User ID for personalized questions
 */
export const getQuestionForPractice = (section, userId, options = {}) => {
    const params = { section };
    if (userId) {
        params.userId = userId;
    }
    if (options.recordId) {
        params.recordId = options.recordId;
    }
    if (options.currentQuestionId) {
        params.currentQuestionId = options.currentQuestionId;
    }
    if (options.queuedQuestionId) {
        params.queuedQuestionId = options.queuedQuestionId;
    }
    return api.get("/questions/practice", { params });
};

/**
 * Get question by ID
 * @param {string} id - Question ID
 */
// export const getQuestionById = (id) =>
//     api.get(`/questions/${id}`);

/**
 * Update a question
 * @param {string} id - Question ID
 * @param {Object} questionData - Updated question data
 */
export const updateQuestion = (id, questionData) =>
    api.put(`/questions/${id}`, questionData);

/**
 * Delete a question
 * @param {string} id - Question ID
 */
export const deleteQuestion = (id) =>
    api.delete(`/questions/${id}`);

// Package APIs
/**
 * Create a new package
 * @param {Object} data - Package data
 */
export const createPackage = (data) =>
    api.post("/packages", data);

/**
 * Get all packages (authenticated)
 */
export const getPackages = () => api.get("/packages");

/**
 * Get active packages without authentication (public pages)
 */
export const getPublicPackages = () => api.get("/packages/public");

/**
 * Get package by ID
 * @param {string} id - Package ID
 */
export const getPackageById = (id) =>
    api.get(`/packages/${id}`);

/**
 * Update a package
 * @param {string} id - Package ID
 * @param {Object} data - Updated package data
 */
export const updatePackage = (id, data) =>
    api.put(`/packages/${id}`, data);

/**
 * Delete a package
 * @param {string} id - Package ID
 */
export const deletePackage = (id) =>
    api.delete(`/packages/${id}`);

// Record APIs
/**
 * Create a new record
 * @param {Object} data - Record data
 */
export const createRecord = (data) =>
    api.post("/records", data);

/**
 * Get all records
 */
export const getRecords = () => api.get("/records");

/**
 * Get record by ID
 * @param {string} id - Record ID
 */
export const getRecordById = (id) =>
    api.get(`/records/${id}`);

/**
 * Update a record
 * @param {string} id - Record ID
 * @param {Object} data - Updated record data
 */
export const updateRecord = (id, data) =>
    api.put(`/records/${id}`, data);

/**
 * Delete a record
 * @param {string} id - Record ID
 */
export const deleteRecord = (id) =>
    api.delete(`/records/${id}`);

// Session Management Record APIs
/**
 * Check if user has an active record for a section
 * @param {string} userId - User ID
 * @param {string} section - Section name
 */
export const checkActiveRecord = (userId, section, sessionMode_type) =>
    api.get("/records/active/check", {
        params: { userId, section, sessionMode_type },
    });

/**
 * Create a new session record
 * @param {Object} data - Record data
 */
export const createNewRecord = (data) =>
    api.post("/records/session/new", data);

/**
 * Pause an active record
 * @param {string} recordId - Record ID
 */
export const pauseRecord = (recordId) =>
    api.patch(`/records/${recordId}/pause`);

/**
 * Resume a paused record
 * @param {string} recordId - Record ID
 */
export const resumeRecord = (recordId) =>
    api.patch(`/records/${recordId}/resume`);

/**
 * End a record session
 * @param {string} recordId - Record ID
 */
export const endRecord = (recordId) =>
    api.patch(`/records/${recordId}/end`);

// User section statistics API
/**
 * Get user statistics by section
 * @param {string} userId - User ID
 */
export const getUserSectionStats = (userId) =>
    api.get(`/records/user/${userId}/section-stats`);

/**
 * Get monthly subject accuracy statistics
 * @param {string} userId - User ID
 */
export const getMonthlySubjectAccuracy = (userId) =>
    api.get(`/records/user/${userId}/monthly-accuracy`);

/**
 * Get daily accuracy trend with optional subject filter
 * @param {string} userId - User ID
 * @param {string} subject - Subject filter: 'all', 'Reading & Writing', or 'Math'
 */
export const getDailyAccuracyTrend = (userId, subject = 'all') =>
    api.get(`/records/user/${userId}/daily-accuracy-trend`, {
        params: { subject },
    });

// Receipt APIs
/**
 * Create a new receipt
 * @param {Object} data - Receipt data
 */
export const createReceipt = (data) =>
    api.post("/receipts", data);

// Test Report APIs
export const createTestReport = (data) =>
    api.post("/test-reports", data);
export const getTestReports = (userId, params = {}) => {
    const queryParams = { ...params };
    return api.get(`/test-reports/user/${userId}`, {
        params: queryParams,
    });
};
export const getTestReportById = (id) =>
    api.get(`/test-reports/${id}`);
export const getLatestTestReport = (
    userId,
    testType = null
) => {
    const params = testType ? { testType } : {};
    return api.get(`/test-reports/user/${userId}/latest`, {
        params,
    });
};
export const getUserTestStats = (userId) =>
    api.get(`/test-reports/user/${userId}/stats`);
export const updateTestReport = (id, data) =>
    api.put(`/test-reports/${id}`, data);
export const deleteTestReport = (id) =>
    api.delete(`/test-reports/${id}`);

// Examination APIs
export const createExamination = (data) =>
    api.post("/examinations", data);
export const getExaminations = () =>
    api.get("/examinations");
export const getExaminationById = (id) =>
    api.get(`/examinations/${id}`);
export const startExamination = (id) =>
    api.post(`/examinations/${id}/start`);
export const getExaminationQuestions = (id, params = {}) =>
    api.get(`/examinations/${id}/questions`, { params });
export const getExaminationTimeStatus = (id) =>
    api.get(`/examinations/${id}/time-status`);
export const submitExaminationAnswer = (id, data) =>
    api.post(`/examinations/${id}/submit-answer`, data);
export const pauseExamination = (id) =>
    api.post(`/examinations/${id}/pause`);
export const completeExamination = (id) =>
    api.post(`/examinations/${id}/complete`);
export const deleteExamination = (id) =>
    api.delete(`/examinations/${id}`);

// Individual question API (lightweight approach)
export const getQuestionById = (questionId) =>
    api.get(`/questions/${questionId}`);

/**
 * Update receipt with PayPal payment method
 * @param {string} id - Receipt ID
 * @param {Object} data - Payment data
 */
export const updateReceiptWithPaypalMethod = (id, data) =>
    api.put(`/receipts/${id}/paypal`, data);

/**
 * Update receipt with Momo payment method
 * @param {string} id - Receipt ID
 * @param {Object} data - Payment data
 */
export const updateReceiptWithMomoMethod = (id, data) =>
    api.put(`/receipts/${id}/momo`, data);

/**
 * Delete a receipt
 * @param {string} id - Receipt ID
 */
export const deleteReceipt = (id) =>
    api.delete(`/receipts/${id}`);

/**
 * Get receipt by ID
 * @param {string} id - Receipt ID
 */
export const getReceiptById = (id) =>
    api.get(`/receipts/${id}`);

/**
 * Get all receipts
 */
export const getReceipts = () => api.get("/receipts");

// ANNOUNCEMENT API
/**
 * Get active announcements for current user
 */
export const getActiveAnnouncements = () => api.get("/announcements/active");

/**
 * Dismiss an announcement
 * @param {string} announcementId - Announcement ID
 */
export const dismissAnnouncement = (announcementId) =>
    api.post("/announcements/dismiss", { announcementId });

// Export chatbot service for use throughout the application
export { chatbotService };
